import React, { useEffect, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { AuthRequired as AuthRequiredType } from 'app/concierge/types';
import useBundleTranslation from 'i18n';
import { IDomain, addAuthorizedTool } from 'store/conciergeSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useHasTokenString } from 'hooks/useHasTokenString';
import type { ITestSession } from 'api/concierge';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { isSameSiteHttpUrl } from 'tools/safeHtml';

interface AuthRequiredProps {
    authRequired: AuthRequiredType;
    onRemoveMessage?: () => void;
}

export const AuthRequired: React.FC<AuthRequiredProps> = ({ authRequired, onRemoveMessage }) => {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();

    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains);
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;
    const testSession = useSelector((state: any) => state.concierge.testSession) as ITestSession | null;
    const roothome = useConfigConstantValue('ROOTHOME') as string | undefined;

    const tool = authRequired.data[0];

    const handleAuth = (authUrl: string) => {
        // Any auth-required block renders this prompt, and the model can write one: open only this site's own
        // sign-in address (security unit 7, decision D3; the rule lives in isSameSiteHttpUrl).
        if (!isSameSiteHttpUrl(authUrl, roothome)) {
            return;
        }
        createTokenInputIfNeeded(tool.tokenFieldName);
        if (isMobile) {
            window.location.href = authUrl;
        } else {
            window.open(authUrl, `Authentication`, 'width=530,height=530');
        }
    };

    const createTokenInputIfNeeded = (tokenFieldName: string) => {
        const existingInput = document.querySelector(`[name="${tokenFieldName}"]`);
        if (!existingInput) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = tokenFieldName;
            input.setAttribute('data-test', tokenFieldName);
            input.value = '';
            document.body.appendChild(input);
        }
    };

    const tokenData = useHasTokenString(tool.tokenFieldName);

    useEffect(() => {
        if (tokenData.hasToken) {
            dispatch(addAuthorizedTool(tool.toolName));
            onRemoveMessage?.();
        }
    }, [tokenData, onRemoveMessage, dispatch, tool.toolName]);

    const relatedDomains = useMemo(() => {
        // A Domain Editor preview asks a domain the panel's list never carries: that list holds
        // switched-on domains only, which is exactly the case this prompt exists for. Take the name
        // from the session instead, or the prompt reads "To answer ,  you need to authorize".
        if (testSession?.scope_kind === 'domain') {
            return testSession.domain_name ? [testSession.domain_name] : [];
        }

        const result: string[] = [];

        availableDomains.forEach((domain) => {
            const serviceExist = domain.customServices?.find((service) => service.name === tool.toolName);
            if (serviceExist) {
                if (selectedDomains && selectedDomains.length > 0) {
                    if (selectedDomains.includes(domain.name)) {
                        result.push(domain.name);
                    }
                } else {
                    result.push(domain.name);
                }
            }
        });

        return result;
    }, [availableDomains, selectedDomains, testSession]);

    // Row 1880: a report answered with each person's own sign-in names that report, never a domain list it is not in.
    const promptLine = tool.sourceName ? (
        <Typography variant="body2">
            {t('auth_required.to_answer_from')}{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
                {tool.sourceName}
            </Box>
            {', '}
            {t('auth_required.sign_in_first', { provider: tool.providerName })}{' '}
            {t('auth_required.expires_in', { days: tool.remainDays })}
        </Typography>
    ) : (
        <Typography variant="body2">
            {t('auth_required.to_answer')}{' '}
            {relatedDomains.map((domain, index) => (
                <React.Fragment key={domain}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                        {domain}
                    </Box>
                    {index < relatedDomains.length - 1 && (
                        <>
                            {relatedDomains.length > 2 && index < relatedDomains.length - 1 ? ', ' : ''}
                            {index === relatedDomains.length - 2 && ` ${t('auth_required.or')} `}
                        </>
                    )}
                    {index == relatedDomains.length - 1 && <>{`, `}</>}
                </React.Fragment>
            ))}{' '}
            {t('auth_required.need_first', { provider: tool.providerName })}{' '}
            {t('auth_required.expires_in', { days: tool.remainDays })}
        </Typography>
    );

    return (
        <Stack spacing={1}>
            {promptLine}
            <Button
                variant="contained"
                onClick={() => handleAuth(tool.authUrl)}
                sx={{ alignSelf: 'flex-start', mb: '8px !important' }}
            >
                {t('auth_required.authorize_button')}
            </Button>
        </Stack>
    );
};
